// Pieces of My Trading Journey shared by its parts: the running-total chart, the firm wheel, tiles,
// where the money went, missing-cost and general-cost notes, the first-steps card for a new
// journey, and Home's Journey card. Numbers come from journey.js.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from './api';
import { CATEGORY_NAMES, isProp, unpaidAccounts } from './business';
import { MILESTONE_TEXT, journeyOverview, milestones } from './journey';
import { useJourney } from './JourneyStory';
import { dayKey, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { Icon } from './Icons';

const whole = (n) => usd.format(Math.round(n)).replace(/\.00$/, '');
// Icons for the Home card's five numbers.
const JOURNEY_ICONS = { calendar: 'calendar', layers: 'wallet', coins: 'money', trophy: 'star', chart: 'chart' };
const signedWhole = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${whole(Math.abs(n))}`;
const monthShort = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', year: '2-digit' });
const monthLong = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' });

// Category colors in a fixed order (never by rank), so a category keeps its color.
const CATEGORY_COLORS = { evaluation: '#1fd7ef', reset: '#ff5d73', activation: '#f5b941', subscription: '#a78bfa', data: '#6f8fb8', platform: '#2fe39a', education: '#f472b6', other: '#93a7bd' };
export const FIRM_COLORS = ['#1fd7ef', '#f5b941', '#a78bfa', '#2fe39a', '#f472b6', '#6f8fb8', '#ff8f5d', '#93a7bd'];

export function Tile({ label, value, note, tone, onClick, open }) {
  const body = (
    <>
      <span className="jo-tile-label">{label}</span>
      <span className={`jo-tile-value${tone ? ` ${tone}` : ''}`}>{value}</span>
      {note && <span className="jo-tile-note">{note}</span>}
    </>
  );
  if (!onClick) return <div className="jo-tile">{body}</div>;
  return <button type="button" className={`jo-tile jo-tile-button${open ? ' open' : ''}`} aria-expanded={open} onClick={onClick}>{body}</button>;
}

// Money out and money in, as running totals: where the lines cross, the journey paid for itself.
// `inLabel` names the money-in line: payouts, or (All together) payouts plus personal trading,
// which can dip below zero.
export function CumulativeChart({ months, breakEvenMonth, inLabel = 'Paid out' }) {
  const { t } = useI18n();
  const W = 760;
  const H = 250;
  const pad = { l: 52, r: 12, t: 16, b: 26 };
  const max = Math.max(1, ...months.map((m) => Math.max(m.spentTotal, m.paidTotal)));
  const min = Math.min(0, ...months.map((m) => m.paidTotal));
  const x = (i) => pad.l + (months.length < 2 ? 0 : (i / (months.length - 1)) * (W - pad.l - pad.r));
  const y = (v) => H - pad.b - ((v - min) / (max - min)) * (H - pad.t - pad.b);
  const line = (key) => months.map((m, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(m[key]).toFixed(1)}`).join(' ');
  const area = `${line('spentTotal')} L${x(months.length - 1)},${y(0)} L${x(0)},${y(0)} Z`;
  const every = Math.max(1, Math.ceil(months.length / 8));
  const be = breakEvenMonth ? months.findIndex((m) => m.month === breakEvenMonth) : -1;
  const [hover, setHover] = useState(null);
  const h = hover === null ? null : months[hover];
  return (
    <div className="jo-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('Money spent and paid out, running totals by month')}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const px = ((e.clientX - r.left) / r.width) * W;
          setHover(Math.max(0, Math.min(months.length - 1, Math.round(((px - pad.l) / (W - pad.l - pad.r)) * (months.length - 1)))));
        }}>
        {[...(min < 0 ? [min] : []), 0, max / 2, max].map((v) => (
          <g key={v}>
            <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} className="jo-grid" />
            <text x={pad.l - 6} y={y(v) + 4} className="jo-axis" textAnchor="end">{v < 0 ? `−${whole(-v)}` : whole(v)}</text>
          </g>
        ))}
        <path d={area} className="jo-spent-area" />
        <path d={line('spentTotal')} className="jo-line spent" />
        <path d={line('paidTotal')} className="jo-line paid" />
        {months.map((m, i) => (i % every === 0 || i === months.length - 1) && (
          <text key={m.month} x={x(i)} y={H - 6} className="jo-axis" textAnchor="middle">{monthShort(m.month)}</text>
        ))}
        {be >= 0 && (
          <g>
            <circle cx={x(be)} cy={y(months[be].paidTotal)} r="5" className="jo-be-dot" />
            <text x={x(be) - 8} y={y(months[be].paidTotal) - 10} className="jo-be-label" textAnchor="end">{t('Break-even')}</text>
          </g>
        )}
        {h && <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} className="jo-cross" />}
      </svg>
      <div className="jo-legend">
        <span><i className="spent" /> {t('Spent')}</span>
        <span><i className="paid" /> {t(inLabel)}</span>
        {h && <span className="jo-hover">{monthLong(h.month)}: {inLabel === 'Paid out' ? t('spent {a}, paid out {b}', { a: whole(h.spentTotal), b: whole(h.paidTotal) })
          : t('spent {a}, payouts and personal trading {b}', { a: whole(h.spentTotal), b: h.paidTotal < 0 ? `−${whole(-h.paidTotal)}` : whole(h.paidTotal) })}</span>}
      </div>
    </div>
  );
}

export function Donut({ rows, total }) {
  const R = 52;
  const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <svg viewBox="0 0 140 140" className="jo-donut" aria-hidden="true">
      <circle cx="70" cy="70" r={R} className="jo-donut-track" />
      {rows.map((r, i) => {
        const len = (r.spent / total) * C;
        const seg = <circle key={r.firm} cx="70" cy="70" r={R} stroke={FIRM_COLORS[i % FIRM_COLORS.length]} strokeDasharray={`${Math.max(0, len - 2)} ${C}`} strokeDashoffset={-offset} className="jo-donut-seg" />;
        offset += len;
        return seg;
      })}
      <text x="70" y="68" textAnchor="middle" className="jo-donut-total">{whole(total)}</text>
    </svg>
  );
}

// --- A new journey ---------------------------------------------------------------------------

const START_HIDDEN = 'mm-journey-start-hidden';
const readHidden = () => {
  try {
    return window.localStorage.getItem(START_HIDDEN) === '1';
  } catch {
    return false;
  }
};
const dayLong = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', day: 'numeric' });

// The first steps of a journey (trades in, what the accounts cost, accounts from before Market
// Matrix) and what unlocks next. Shown until the steps are done and the journey is two months
// old, or the trader hides it.
export function JourneyStart({ o, accounts, trades, expenses, onAddPast }) {
  const { t } = useI18n();
  const { openLog } = useJourney();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(readHidden);
  const unpaid = unpaidAccounts(accounts.filter((a) => !a.attemptOf), expenses)[0];
  const past = accounts.some((a) => isProp(a) && a.archived);
  const steps = [
    {
      id: 'trades', done: trades.length > 0,
      title: t('Bring in your trades'),
      text: trades.length ? t(trades.length === 1 ? '{n} trade in your journal.' : '{n} trades in your journal.', { n: trades.length.toLocaleString(currentLocale()) })
        : t('Sync or import an account. Every trade becomes part of your story.'),
      action: !trades.length && <button type="button" className="small" onClick={() => navigate('/app/accounts/new')}>{t('Add an account')}</button>,
    },
    {
      id: 'cost', done: expenses.length > 0,
      title: t('Log what your accounts cost'),
      text: expenses.length ? t('Your spending is in. Keep adding fees, resets and tools as they come.')
        : t('Evaluation fees, resets and tools. That’s how your journey knows when it pays for itself.'),
      action: !expenses.length && openLog && (
        <button type="button" className="small" onClick={() => openLog('expense', unpaid ? { accountId: unpaid._id } : {})}>
          {unpaid ? t('What did {name} cost?', { name: unpaid.name }) : t('Log what you paid')}
        </button>
      ),
    },
    {
      id: 'past', done: past, optional: true,
      title: t('Add accounts from before Market Matrix'),
      text: past ? t('Your earlier accounts are part of the story.')
        : t('Evaluations you blew or passed before you joined count too: in your money, your lessons and your pass rate.'),
      action: !past && onAddPast && <button type="button" className="secondary small" onClick={onAddPast}>+ {t('Add a past account')}</button>,
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const young = !o || o.months.length <= 2;
  // With nothing in yet the card is the whole page, so it can't be hidden.
  const empty = !trades.length && !expenses.length && !accounts.length;
  if (!empty && (hidden || (doneCount === steps.length && !young))) return null;

  // Coming up: Then vs now unlocks 60 days after the first trade; the closest milestone.
  const first = o ? o.firstDay : null;
  const ready = first ? new Date(`${first}T12:00:00`) : null;
  if (ready) ready.setDate(ready.getDate() + 60);
  const readyKey = ready ? dayKey(ready) : null;
  const all = trades.length ? milestones(accounts, trades) : [];
  const next = all.filter((m) => !m.earnedOn && m.need > 1 && m.have > 0 && MILESTONE_TEXT[m.id]).sort((a, b) => b.have / b.need - a.have / a.need)[0];
  const earned = all.filter((m) => m.earnedOn).length;

  function hide() {
    try {
      window.localStorage.setItem(START_HIDDEN, '1');
    } catch {
      // Private mode: it hides until the page reloads.
    }
    setHidden(true);
  }

  return (
    <section className="jo-card jo-start" aria-labelledby="jo-start-title">
      <div className="jo-card-head">
        <div>
          <span className="jo-tile-label">{t('Step {n} of {total}', { n: Math.min(doneCount + 1, steps.length), total: steps.length })}</span>
          <h2 id="jo-start-title">{t('Your journey starts here')}</h2>
          <p className="field-hint">{t('Every trade, every account and every dollar, from your first evaluation to your first payout. Three steps and it starts telling your story.')}</p>
        </div>
        {trades.length > 0 && <button type="button" className="link-button" onClick={hide}>{t('Hide')}</button>}
      </div>
      <ol className="jo-steps">
        {steps.map((s) => (
          <li key={s.id} className={s.done ? 'done' : ''}>
            <span className="jo-step-mark" aria-hidden="true">{s.done ? '✓' : ''}</span>
            <div>
              <strong>{s.title}{s.optional && !s.done && <span className="jo-step-opt"> · {t('optional')}</span>}</strong>
              <p>{s.text}</p>
            </div>
            {s.action || null}
            {s.done && <span className="visually-hidden">{t('Done')}</span>}
          </li>
        ))}
      </ol>
      {trades.length > 0 && (
        <div className="jo-coming">
          <span className="jo-tile-label">{t('Coming up')}</span>
          <ul>
            {readyKey && (
              <li>
                <span aria-hidden="true">🔁</span>
                {readyKey <= dayKey(new Date()) ? <Link to="/app/journey/better">{t('Then vs now is ready')} &rarr;</Link>
                  : t('Then vs now unlocks on {day}: your first 30 days next to your latest 30.', { day: dayLong(readyKey) })}
              </li>
            )}
            {next && (
              <li>
                <span aria-hidden="true">{MILESTONE_TEXT[next.id][0]}</span>
                {t('Next milestone: {name}, {have} of {need}.', { name: t(MILESTONE_TEXT[next.id][1]), have: next.money ? whole(next.have) : next.have.toLocaleString(currentLocale()), need: next.money ? whole(next.need) : next.need.toLocaleString(currentLocale()) })}
              </li>
            )}
            {earned > 0 && <li><span aria-hidden="true">🏅</span><Link to="/app/journey/edge-milestones">{t(earned === 1 ? '{n} milestone earned' : '{n} milestones earned', { n: earned })} &rarr;</Link></li>}
          </ul>
        </div>
      )}
    </section>
  );
}

// Home's doorway into the Journey: how long, how many accounts, and where the money stands.
export function JourneyHomeCard({ accounts, trades }) {
  const { t } = useI18n();
  const [expenses, setExpenses] = useState(null);
  useEffect(() => {
    const load = () => api('/expenses').then(setExpenses).catch(() => setExpenses((list) => list || []));
    load();
    // Spending added elsewhere on the page (the monthly rebill check) updates the numbers.
    window.addEventListener('mm-expenses-changed', load);
    return () => window.removeEventListener('mm-expenses-changed', load);
  }, []);
  const o = useMemo(() => (expenses ? journeyOverview(accounts, trades, expenses) : null), [accounts, trades, expenses]);
  if (!o) return null;
  const months = o.months.length;
  const tiles = [
    ['calendar', months, t(months === 1 ? 'Month' : 'Months'), ''],
    ['layers', o.accounts.bought, t(o.accounts.bought === 1 ? 'Prop account' : 'Prop accounts'), ''],
    ['coins', o.spent > 0 ? whole(o.spent) : '—', t('Total spent'), ''],
    ['trophy', o.paid > 0 ? whole(o.paid) : '—', t('Payouts'), o.paid > 0 ? 'gain' : ''],
    ['chart', o.spent > 0 || o.paid > 0 ? signedWhole(o.net) : '—', t('Net journey'), o.spent > 0 || o.paid > 0 ? (o.net >= 0 ? 'gain' : 'loss') : ''],
  ];
  return (
    <section className="home-journey home-journey-card">
      <div className="home-journey-head">
        <span className="home-journey-label">{t('My Trading Journey')}</span>
        <Link to="/app/journey/profitable" className="home-journey-go">{t('Open your journey')} &rarr;</Link>
      </div>
      <p className="home-journey-text">{t('Your whole trading journey: progress, setbacks, spending and payouts.')}</p>
      <Link to="/app/journey/profitable" className="home-journey-tiles">
        {tiles.map(([icon, value, label, tone]) => (
          <span key={label} className="home-journey-tile">
            <span className={`home-journey-icon icon-${icon}`} aria-hidden="true"><Icon name={JOURNEY_ICONS[icon]} /></span>
            <strong className={tone}>{value}</strong>
            <span>{label}</span>
          </span>
        ))}
      </Link>
    </section>
  );
}

// Where the money went, biggest category first.
export function WhereMoneyWent({ cats, hint = 'Your evaluations, resets, activation fees, data and courses show here, biggest first.', onPick }) {
  const { t } = useI18n();
  const catMax = Math.max(1, ...cats.map((c) => c.amount));
  return (
    <section className="jo-card">
          <h2>{t('Where the money went')}</h2>
          {!cats.length && <p className="field-hint">{t(hint)}</p>}
          <ul className="jo-cats">
            {cats.map((c) => (
              <li key={c.category}>
                <span><i style={{ background: CATEGORY_COLORS[c.category] || CATEGORY_COLORS.other }} />{onPick
                  ? <button type="button" className="link-button" onClick={() => onPick(c.category)}>{t(CATEGORY_NAMES[c.category] || c.category)}</button>
                  : t(CATEGORY_NAMES[c.category] || c.category)}</span>
                <span className="jo-cat-bar"><b style={{ width: `${(c.amount / catMax) * 100}%`, background: CATEGORY_COLORS[c.category] || CATEGORY_COLORS.other }} /></span>
                <strong>{whole(c.amount)}</strong>
              </li>
            ))}
          </ul>
        </section>
  );
}

// Prop accounts with a payout but nothing logged for what they cost: the money totals are
// understated until it's in, so the page says so where the numbers are.
export function MissingCosts({ history }) {
  const { t } = useI18n();
  const { openLog } = useJourney();
  const missing = history.filter((h) => h.payouts > 0 && !h.spent);
  if (!missing.length) return null;
  return (
    <p className="jo-general jo-missing-note">
      {t(missing.length === 1 ? '{name} has a payout but no cost logged, so what you spent is understated.' : '{n} accounts have a payout but no cost logged, so what you spent is understated.', { name: missing[0].account.name, n: missing.length })}{' '}
      {openLog && <button type="button" className="link-button" onClick={() => openLog('expense', { accountId: missing[0].account._id })}>{t('Log what {name} cost', { name: missing[0].account.name })}</button>}
    </p>
  );
}

// Costs not tied to an account (courses, tools) count only in All together; the prop and
// personal views say so, with a way over.
export function GeneralCosts({ amount, onScope }) {
  const { t } = useI18n();
  if (!amount || !onScope) return null;
  return (
    <p className="jo-general">
      {t('Not counted here: {amount} in costs you marked for both sides.', { amount: whole(amount) })}{' '}
      <button type="button" className="link-button" onClick={() => onScope('all')}>{t('See All together')}</button>
    </p>
  );
}
